import React from 'react';
import { PageHero } from '../components/ui/PageHero';
import { Container, Eyebrow, Heading } from '../components/ui/Section';
import { CtaBand } from '../components/ui/CtaBand';
import { DocumentPlatform } from '../components/features/DocumentPlatform';
import { SatelliteView } from '../components/features/SatelliteView';
import { Faq } from '../components/ui/Faq';
import { IMAGES, TECH_CAPABILITIES } from '../data/solutions';
import { faqJsonLd, serviceJsonLd, useSeo } from '../utils/seo';
const DOC_QUERIES = ['Búsqueda por patente', 'Búsqueda por número interno', 'Documentos requeridos', 'Documentos pendientes', 'Documentos verificados', 'Vencimientos', 'Utilización', 'Frecuencia requerida', 'Documentación invalidante', 'Estado general de la unidad'];
const FAQS = [{
  question: '¿Qué se puede consultar en la plataforma documental?',
  answer: 'El estado documental de flota, transportista, chofer, camión y acoplado: documentos requeridos, pendientes y verificados, vencimientos, frecuencia requerida, documentación invalidante y estado general de la unidad, con búsqueda por patente o número interno.'
}, {
  question: '¿Hay que cambiar de proveedor de monitoreo satelital?',
  answer: 'No. El modelo tecnológico puede integrar distintos proveedores dentro de una vista operacional única, conservando el que el cliente ya utiliza.'
}, {
  question: '¿Trabajan sobre TMS y WMS?',
  answer: 'Sí, propios o del cliente, además de herramientas de gestión, macros, integraciones y reportes construidos sobre la operación real.'
}];
const CRUMBS = [{
  name: 'Inicio',
  path: '/'
}, {
  name: 'Tecnología',
  path: '/tecnologia'
}];
export function Technology() {
  useSeo({
    title: 'Control documental de camiones y monitoreo de flota | Tecnología — Rabemar',
    description: 'Plataforma de control documental de flota, transportistas, choferes y unidades, monitoreo satelital multiplataforma, dashboards, KPIs, BI y automatización operativa.',
    path: '/tecnologia',
    image: IMAGES.controlTower,
    breadcrumbs: CRUMBS,
    jsonLd: [serviceJsonLd('Tecnología y control documental para operaciones', 'Plataforma de control documental de flota y transportistas, monitoreo satelital multiplataforma, dashboards, KPIs, Business Intelligence e integraciones.', '/tecnologia'), faqJsonLd(FAQS)]
  });
  return <>
      <PageHero eyebrow="Tecnología & Control Documental" title="Tecnología aplicada a problemas operativos reales." lead="Sin abstracciones: plataformas, tableros e integraciones que resuelven documentación, seguimiento, indicadores y planificación de la operación." image={IMAGES.controlTower} imageAlt="Sala de operaciones con pantallas mostrando mapas de rutas y tableros de indicadores logísticos" crumbs={CRUMBS} ctaLabel="Hablar con un especialista" ctaService="Tecnología" data-id="element-855" data-mp="3r1quqv7bw3hg" />

      <section className="bg-white py-20 md:py-28" data-id="element-856" data-mp="h8745bd7co5f">
        <Container data-id="element-857" data-mp="h5b9px0kchlq">
          <div className="max-w-3xl" data-id="element-858" data-mp="m5pglbqfhli6">
            <Eyebrow data-id="element-859" data-mp="m6qsfif9qc0y">Plataforma documental</Eyebrow>
            <Heading as="h2" size="lg" className="mt-5" data-id="element-860" data-mp="q82kdulm79cb">
              Cada unidad habilitada.
              <br data-id="element-861" data-mp="uyalpp14ztoj" />
              Cada documento bajo control.
            </Heading>
            <p className="mt-6 text-lg leading-relaxed text-graphite-500" data-id="element-862" data-mp="v0dnt7fkdd9m">
              La plataforma concentra el estado documental de la flota y de la red de transportistas, con criterios de
              habilitación explícitos: una unidad con documentación invalidante pendiente no opera.
            </p>
          </div>

          <div className="mt-12" data-id="element-863" data-mp="1dxwy0edrd1xm">
            <DocumentPlatform data-id="element-864" data-mp="1dw3makoafrir" />
          </div>

          <ul className="mt-10 grid gap-px bg-mist-200 sm:grid-cols-2 lg:grid-cols-5" data-id="element-865" data-mp="3bn6e7d41q8qg">
            {DOC_QUERIES.map(q => <li key={q} className="bg-white px-5 py-4 text-sm text-graphite-500" data-id="element-866" data-mp="3fo01q5dktlit">
                {q}
              </li>)}
          </ul>
        </Container>
      </section>

      <section className="bg-graphite-900 py-20 md:py-28" data-id="element-867" data-mp="2gdjawfwokkxu">
        <Container data-id="element-868" data-mp="2gf2p80ys0lwz">
          <div className="max-w-3xl" data-id="element-869" data-mp="2ucr8x5h3xn0g">
            <Eyebrow tone="dark" data-id="element-870" data-mp="2ubq077nkdclw">Monitoreo satelital</Eyebrow>
            <Heading as="h2" size="lg" tone="dark" className="mt-5" data-id="element-871" data-mp="2pdxwlgdk421v">
              Una vista única de toda la operación.
            </Heading>
          </div>
          <div className="mt-12" data-id="element-872" data-mp="21on6qtxqvs2i">
            <SatelliteView data-id="element-873" data-mp="1wp0sqia3e7sn" />
          </div>
        </Container>
      </section>

      <section className="bg-white py-20 md:py-28" data-id="element-874" data-mp="1wmxsl5f86ya7">
        <Container data-id="element-875" data-mp="2be6r6413vtzh">
          <div className="max-w-3xl" data-id="element-876" data-mp="2bfhqnrniqfks">
            <Eyebrow data-id="element-877" data-mp="26n6rws4ikztt">Capacidades transversales</Eyebrow>
            <Heading as="h2" size="md" className="mt-5" data-id="element-878" data-mp="26mexvlqkfa5n">
              La tecnología atraviesa todas las unidades de negocio.
            </Heading>
          </div>
          <dl className="mt-12 grid gap-px bg-mist-200 md:grid-cols-3" data-id="element-879" data-mp="1dz157ztwh0rc">
            {TECH_CAPABILITIES.map(cap => <div key={cap.title} className="bg-white p-7" data-id="element-880" data-mp="3pktjpap08lri">
                <dt className="font-display text-lg font-bold tracking-tight text-graphite-900" data-id="element-881" data-mp="3ucl22xy0bl6k">{cap.title}</dt>
                <dd className="mt-3 text-sm leading-relaxed text-graphite-500" data-id="element-882" data-mp="3glq1e8xnwae2">{cap.text}</dd>
              </div>)}
          </dl>
        </Container>
      </section>

      <section className="bg-mist-50 py-20 md:py-28" data-id="element-883" data-mp="3kkf9blzxkzqb">
        <Container data-id="element-884" data-mp="c89puga96ssq">
          <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16" data-id="element-885" data-mp="c7riani54s5i">
            <div data-id="element-886" data-mp="h4rs99i42f1w">
              <Eyebrow data-id="element-887" data-mp="h59ucmrgfnhq">Preguntas frecuentes</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-888" data-mp="36th0cz36yqm">
                Tecnología & Control Documental
              </Heading>
            </div>
            <Faq items={FAQS} data-id="element-889" data-mp="83jvivv8tm9x" />
          </div>
        </Container>
      </section>

      <CtaBand title="¿Su operación tiene visibilidad suficiente?" text="Revisamos qué información existe hoy, qué falta y qué tablero necesita para decidir a tiempo." primaryLabel="Hablar con un especialista" location="tecnologia-cta" service="Tecnología" data-id="element-890" data-mp="2nuxwmn2j6df4" />
    </>;
}